import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Plus, Minus, ChevronRight, ChevronLeft, ShoppingCart, User, UserX, Package, Banknote, CreditCard, ToggleLeft, ToggleRight, Check, FileText } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { getProducts, getCustomers, createQuickSale, createProduct, getParasutSettings, createBatchInvoice } from '../services/api';
import { toast } from 'sonner';

const QuickSaleModal = ({ open, onClose }) => {
  const [step, setStep] = useState(1);

  // Step 1: Products
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [qtySelect, setQtySelect] = useState(1);
  const [cart, setCart] = useState([]);
  const [showQuickProduct, setShowQuickProduct] = useState(false);
  const [quickProductName, setQuickProductName] = useState('');
  const [quickProductPrice, setQuickProductPrice] = useState('');
  const [quickProductBarcode, setQuickProductBarcode] = useState('');
  const searchRef = useRef(null);

  // Step 2: Customer
  const [customerMode, setCustomerMode] = useState(null); // null, 'skip', 'select'
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isCredit, setIsCredit] = useState(false);

  // Step 3: Payment
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashAmount, setCashAmount] = useState('');
  const [cardAmount, setCardAmount] = useState('');
  const [creditCollected, setCreditCollected] = useState('');
  const [creditMethod, setCreditMethod] = useState('cash');
  const [saving, setSaving] = useState(false);

  // Invoice
  const [createInvoice, setCreateInvoice] = useState(false);
  const [tcKimlikNo, setTcKimlikNo] = useState('');
  const [parasutActive, setParasutActive] = useState(false);

  useEffect(() => {
    if (open) {
      getProducts().then(r => setProducts(r.data)).catch(() => {});
      setStep(1); setCart([]); setQuery(''); setQtySelect(1);
      setCustomerMode(null); setSelectedCustomer(null); setIsCredit(false);
      setPaymentMethod('cash'); setCashAmount(''); setCardAmount('');
      setCreditCollected(''); setCreditMethod('cash');
      setShowQuickProduct(false);
      setCreateInvoice(false); setTcKimlikNo('');
      getParasutSettings().then(r => {
        const ps = r.data;
        setParasutActive(ps?.is_active && ps?.client_id && ps?.firma_no ? true : false);
      }).catch(() => {});
    }
  }, [open]);

  // Auto-focus search
  useEffect(() => {
    if (open && step === 1 && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open, step]);

  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  // Search
  const filtered = query.length >= 1
    ? products.filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        (p.barcode && p.barcode.includes(query))
      ).slice(0, 10)
    : [];

  const exactBarcode = query.length >= 3
    ? products.find(p => p.barcode && p.barcode === query)
    : null;

  const addToCart = useCallback((product, qty) => {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) {
        return prev.map(i => i.product_id === product.id
          ? { ...i, quantity: i.quantity + qty }
          : i
        );
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.default_price || 0,
        quantity: qty,
        barcode: product.barcode,
        stock: product.current_stock,
        vat_rate: product.vat_rate,
      }];
    });
    setQuery('');
    setQtySelect(1);
    setTimeout(() => searchRef.current?.focus(), 50);
  }, []);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (exactBarcode) {
        addToCart(exactBarcode, qtySelect);
      } else if (filtered.length === 1) {
        addToCart(filtered[0], qtySelect);
      }
    }
  };

  const updateCartQty = (pid, delta) => {
    setCart(prev => prev.map(i => {
      if (i.product_id !== pid) return i;
      const newQty = Math.max(1, i.quantity + delta);
      return { ...i, quantity: newQty };
    }));
  };

  const updateCartPrice = (pid, val) => {
    setCart(prev => prev.map(i =>
      i.product_id === pid ? { ...i, price: parseFloat(val) || 0 } : i
    ));
  };

  const removeFromCart = (pid) => {
    setCart(prev => prev.filter(i => i.product_id !== pid));
  };

  // Customer search
  useEffect(() => {
    if (customerQuery.length < 2) { setCustomerResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await getCustomers(customerQuery);
        setCustomerResults(r.data);
      } catch (e) {}
    }, 300);
    return () => clearTimeout(t);
  }, [customerQuery]);

  // Quick product add
  const handleQuickProductAdd = async () => {
    if (!quickProductName.trim()) { toast.error('Ürün adı gerekli'); return; }
    try {
      const res = await createProduct({
        name: quickProductName.trim(),
        barcode: quickProductBarcode || undefined,
        default_price: parseFloat(quickProductPrice) || 0,
        current_stock: 100,
        unit: 'adet',
      });
      const newProduct = {
        id: res.data.id,
        name: quickProductName.trim(),
        barcode: quickProductBarcode,
        default_price: parseFloat(quickProductPrice) || 0,
        current_stock: 100,
      };
      setProducts(prev => [...prev, newProduct]);
      addToCart(newProduct, qtySelect);
      setShowQuickProduct(false);
      setQuickProductName(''); setQuickProductPrice(''); setQuickProductBarcode('');
      toast.success('Ürün eklendi');
    } catch (e) { toast.error('Ürün eklenemedi'); }
  };

  // Submit
  const handleSubmit = async () => {
    if (createInvoice && tcKimlikNo.length !== 11) {
      toast.error('Fatura kesmek için geçerli bir TC Kimlik No giriniz (11 hane)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        items: cart.map(i => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
        })),
        customer_id: selectedCustomer?.id || null,
        payment_method: isCredit ? (creditMethod || 'cash') : paymentMethod,
        cash_amount: paymentMethod === 'split' ? parseFloat(cashAmount) || 0 : (paymentMethod === 'cash' ? total : 0),
        card_amount: paymentMethod === 'split' ? parseFloat(cardAmount) || 0 : (paymentMethod === 'card' ? total : 0),
        is_credit: isCredit,
        credit_collected: isCredit ? parseFloat(creditCollected) || 0 : 0,
        credit_collected_method: isCredit ? creditMethod : 'cash',
      };
      await createQuickSale(payload);

      if (createInvoice && parasutActive && selectedCustomer) {
        try {
          const invoiceItems = cart.map(c => ({
            name: c.name,
            price: c.price,
            quantity: c.quantity,
            vat_rate: c.vat_rate != null ? parseFloat(c.vat_rate) : 20,
          }));
          await createBatchInvoice({
            customer_id: selectedCustomer.id,
            tc_kimlik_no: tcKimlikNo,
            items: invoiceItems,
          });
          toast.success('Fatura oluşturuldu');
        } catch (e) {
          toast.error('Satış kaydedildi ama fatura oluşturulamadı: ' + (e.response?.data?.detail || ''));
        }
      }

      toast.success(`${cart.length} ürün satıldı`);
      onClose(true);
    } catch (e) {
      toast.error('Satış kaydedilemedi');
    }
    setSaving(false);
  };

  if (!open) return null;

  const veresiyeRemaining = total - (parseFloat(creditCollected) || 0);

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-4" data-testid="quick-sale-modal">
      <div className="absolute inset-0 bg-black/40" onClick={() => onClose(false)} />
      <div className="relative w-full sm:max-w-md lg:max-w-3xl bg-white sm:rounded-2xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] shadow-2xl">

        {/* Header */}
        <div className="px-4 lg:px-6 pt-4 pb-3 flex items-center justify-between border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} className="p-1 rounded-full hover:bg-gray-100" data-testid="quick-sale-back">
                <ChevronLeft size={20} className="text-[#8E8E93]" />
              </button>
            )}
            <h2 className="text-base lg:text-lg font-bold text-gray-900">
              {step === 1 ? 'Ürün Seç' : step === 2 ? 'Müşteri' : 'Ödeme'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#8E8E93]">{step}/3</span>
            <button onClick={() => onClose(false)} className="p-1.5 rounded-full hover:bg-gray-100" data-testid="quick-sale-close">
              <X size={22} className="text-[#8E8E93]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">

          {/* STEP 1: Products */}
          {step === 1 && (
            <div className="p-4 lg:p-6 space-y-3">
              {/* Search with qty selector */}
              <div className="flex gap-2">
                <div className="relative shrink-0">
                  <select
                    value={qtySelect}
                    onChange={e => setQtySelect(parseInt(e.target.value))}
                    className="w-[4.5rem] h-11 text-center text-sm font-semibold bg-[#F2F2F7] border-0 rounded-xl appearance-none px-2"
                    data-testid="qty-selector"
                  >
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <option key={n} value={n}>{n} adet</option>
                    ))}
                  </select>
                </div>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={16} />
                  <Input
                    ref={searchRef}
                    type="search"
                    placeholder="Barkod veya ürün adı..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="pl-9 h-11 text-sm bg-[#F2F2F7] border-0 rounded-xl"
                    data-testid="product-search-input"
                  />
                </div>
              </div>

              {/* Search results */}
              {query.length >= 1 && (
                <div className="space-y-1">
                  {filtered.length > 0 ? filtered.map(p => (
                    <button key={p.id} onClick={() => addToCart(p, qtySelect)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-[#F2F2F7] rounded-xl hover:bg-gray-200 transition-colors text-left"
                      data-testid={`product-result-${p.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                        {p.barcode && <span className="text-[10px] text-[#8E8E93]">{p.barcode}</span>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="text-xs text-[#8E8E93]">Stok: {p.current_stock}</span>
                        <span className="text-sm font-bold text-[#34C759]">{p.default_price > 0 ? `${p.default_price} TL` : '-'}</span>
                        <Plus size={16} className="text-[#6b6de3]" />
                      </div>
                    </button>
                  )) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-[#8E8E93] mb-2">Ürün bulunamadı</p>
                      {!showQuickProduct && (
                        <Button onClick={() => { setShowQuickProduct(true); setQuickProductName(query); }}
                          className="bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl text-xs h-9" data-testid="quick-add-product-btn">
                          <Plus size={14} className="mr-1" /> Hızlı Ürün Ekle
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Quick product form */}
              {showQuickProduct && (
                <div className="bg-blue-50 rounded-xl p-3 space-y-2" data-testid="quick-product-form">
                  <Input placeholder="Ürün adı *" value={quickProductName} onChange={e => setQuickProductName(e.target.value)}
                    className="h-10 text-sm bg-white border-0 rounded-xl" autoFocus />
                  <div className="flex gap-2">
                    <Input placeholder="Barkod" value={quickProductBarcode} onChange={e => setQuickProductBarcode(e.target.value)}
                      className="h-10 text-sm bg-white border-0 rounded-xl flex-1" />
                    <div className="relative w-24">
                      <Input type="number" placeholder="Fiyat" value={quickProductPrice} onChange={e => setQuickProductPrice(e.target.value)}
                        className="h-10 text-sm bg-white border-0 rounded-xl pr-7 text-right" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#8E8E93]">TL</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" className="flex-1 h-9 text-sm rounded-xl" onClick={() => setShowQuickProduct(false)}>Vazgeç</Button>
                    <Button className="flex-1 h-9 text-sm bg-[#6b6de3] rounded-xl" onClick={handleQuickProductAdd}>Ekle</Button>
                  </div>
                </div>
              )}

              {/* Cart */}
              {cart.length > 0 && (
                <div className="space-y-1" data-testid="quick-sale-cart">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-[#8E8E93] flex items-center gap-1">
                      <ShoppingCart size={12} /> Sepet ({cart.length})
                    </span>
                    <span className="text-sm font-bold text-gray-900">{total.toFixed(0)} TL</span>
                  </div>
                  {cart.map(item => (
                    <div key={item.product_id} className="flex items-center gap-1.5 bg-[#F2F2F7] rounded-xl px-2.5 py-2" data-testid={`cart-item-${item.product_id}`}>
                      <Package size={14} className="text-[#FF9500] shrink-0" />
                      <span className="text-xs font-medium text-gray-900 truncate flex-1 min-w-0">{item.name}</span>
                      <div className="flex items-center bg-white rounded-md shrink-0">
                        <button onClick={() => updateCartQty(item.product_id, -1)} className="w-6 h-6 flex items-center justify-center text-[#8E8E93]"><Minus size={11} /></button>
                        <span className="w-5 text-center text-xs font-semibold">{item.quantity}</span>
                        <button onClick={() => updateCartQty(item.product_id, 1)} className="w-6 h-6 flex items-center justify-center text-[#6b6de3]"><Plus size={11} /></button>
                      </div>
                      <div className="relative w-[5.5rem] shrink-0">
                        <Input type="number" value={item.price} onChange={e => updateCartPrice(item.product_id, e.target.value)}
                          className="h-7 text-xs bg-white border-0 rounded-md pr-5 text-right" />
                        <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[8px] text-[#8E8E93]">TL</span>
                      </div>
                      <button onClick={() => removeFromCart(item.product_id)} className="p-1 text-[#C7C7CC] hover:text-red-500 shrink-0">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Customer */}
          {step === 2 && (
            <div className="p-4 lg:p-6 space-y-3">
              {!customerMode ? (
                <div className="space-y-2">
                  <button onClick={() => { setCustomerMode('skip'); setStep(3); }}
                    className="w-full p-4 bg-[#F2F2F7] rounded-2xl flex items-center gap-3 hover:bg-gray-200 transition-colors"
                    data-testid="customer-skip-btn">
                    <div className="w-11 h-11 bg-gray-200 rounded-xl flex items-center justify-center">
                      <UserX size={22} className="text-[#8E8E93]" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900 text-sm">Müşteri Seçmeden Devam Et</p>
                      <p className="text-xs text-[#8E8E93]">Hızlı satış olarak kaydedilir</p>
                    </div>
                    <ChevronRight size={20} className="text-[#C7C7CC] ml-auto" />
                  </button>
                  <button onClick={() => setCustomerMode('select')}
                    className="w-full p-4 bg-[#F2F2F7] rounded-2xl flex items-center gap-3 hover:bg-gray-200 transition-colors"
                    data-testid="customer-select-btn">
                    <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                      <User size={22} className="text-[#6b6de3]" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900 text-sm">Müşteri Seç</p>
                      <p className="text-xs text-[#8E8E93]">Satış müşteriye bağlanır</p>
                    </div>
                    <ChevronRight size={20} className="text-[#C7C7CC] ml-auto" />
                  </button>
                </div>
              ) : customerMode === 'select' && !selectedCustomer ? (
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={16} />
                    <Input
                      type="search"
                      placeholder="Müşteri Ara (İsim, telefon veya TCKN)"
                      value={customerQuery}
                      onChange={e => setCustomerQuery(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && customerResults.length === 1) { setSelectedCustomer(customerResults[0]); } }}
                      className="pl-9 h-11 text-sm bg-[#F2F2F7] border-0 rounded-xl"
                      autoFocus
                      data-testid="customer-search-input"
                    />
                  </div>
                  {customerResults.map(c => (
                    <button key={c.id} onClick={() => { setSelectedCustomer(c); }}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-[#F2F2F7] rounded-xl hover:bg-gray-200 text-left"
                      data-testid={`customer-result-${c.id}`}>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{c.name}</p>
                        <p className="text-xs text-[#8E8E93]">{c.phone}</p>
                      </div>
                      <ChevronRight size={18} className="text-[#C7C7CC]" />
                    </button>
                  ))}
                  <button onClick={() => { setCustomerMode(null); setCustomerQuery(''); setCustomerResults([]); }}
                    className="text-xs text-[#6b6de3] mt-2">Geri dön</button>
                </div>
              ) : customerMode === 'select' && selectedCustomer ? (
                <div className="space-y-3">
                  <div className="bg-blue-50 rounded-2xl p-4 flex items-center gap-3" data-testid="selected-customer-info">
                    <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center">
                      <User size={22} className="text-[#6b6de3]" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm">{selectedCustomer.name}</p>
                      <p className="text-xs text-[#8E8E93]">{selectedCustomer.phone}</p>
                    </div>
                    <button onClick={() => { setSelectedCustomer(null); setIsCredit(false); }} className="text-xs text-[#FF3B30]">Değiştir</button>
                  </div>

                  {/* Veresiye toggle */}
                  <div className="flex items-center justify-between bg-[#F2F2F7] rounded-2xl p-4" data-testid="veresiye-toggle-section">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Veresiye</p>
                      <p className="text-[10px] text-[#8E8E93]">Borç olarak müşteriye yansır</p>
                    </div>
                    <button onClick={() => setIsCredit(!isCredit)} data-testid="veresiye-toggle">
                      {isCredit
                        ? <ToggleRight size={36} className="text-[#FF9500]" />
                        : <ToggleLeft size={36} className="text-[#C7C7CC]" />}
                    </button>
                  </div>

                  <Button onClick={() => setStep(3)} className="w-full h-12 bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl text-sm font-semibold" data-testid="customer-continue-btn">
                    Devam Et <ChevronRight size={16} className="ml-1" />
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          {/* STEP 3: Payment */}
          {step === 3 && (
            <div className="p-4 lg:p-6 space-y-4">
              {/* Summary */}
              <div className="bg-[#F2F2F7] rounded-2xl p-4 space-y-1" data-testid="payment-summary">
                <div className="flex justify-between text-sm">
                  <span className="text-[#8E8E93]">Toplam</span>
                  <span className="font-bold text-gray-900">{total.toFixed(0)} TL</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-[#8E8E93]">{cart.length} ürün</span>
                  <span className="text-[#8E8E93]">
                    {selectedCustomer ? selectedCustomer.name : 'Hızlı Satış'}
                  </span>
                </div>
              </div>

              {/* Fatura Kes Toggle */}
              {parasutActive && selectedCustomer && (
                <div className="bg-[#F2F2F7] rounded-2xl p-4" data-testid="sale-invoice-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Fatura Kes</p>
                      <p className="text-xs text-[#8E8E93]">Paraşüt ile e-Fatura / e-Arşiv</p>
                    </div>
                    <button onClick={() => setCreateInvoice(!createInvoice)} data-testid="sale-invoice-toggle">
                      {createInvoice
                        ? <ToggleRight size={36} className="text-[#6366F1]" />
                        : <ToggleLeft size={36} className="text-[#C7C7CC]" />}
                    </button>
                  </div>
                  {createInvoice && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <label className="text-xs text-[#8E8E93] mb-1 block">TC Kimlik No <span className="text-red-500">*</span></label>
                      <Input type="text" inputMode="numeric" maxLength={11} value={tcKimlikNo}
                        onChange={e => setTcKimlikNo(e.target.value.replace(/\D/g, '').slice(0, 11))}
                        placeholder="11 haneli TC Kimlik No"
                        className="h-12 bg-white border-0 rounded-xl" data-testid="sale-tc-kimlik-input" />
                      {tcKimlikNo && tcKimlikNo.length !== 11 && (
                        <p className="text-xs text-red-500 mt-1">TC Kimlik No 11 haneli olmalıdır</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Veresiye payment */}
              {isCredit ? (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-[#FF9500]">Veresiye Ödeme</p>
                  <div>
                    <label className="text-xs text-[#8E8E93] mb-1 block">Tahsil Edilen Tutar</label>
                    <div className="relative">
                      <Input type="number" placeholder="0" value={creditCollected}
                        onChange={e => setCreditCollected(e.target.value)}
                        className="h-12 text-lg bg-[#F2F2F7] border-0 rounded-xl pr-8 text-right font-bold"
                        data-testid="credit-collected-input" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8E8E93]">TL</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {[{ val: 'cash', label: 'Nakit', icon: Banknote }, { val: 'card', label: 'Kart', icon: CreditCard }].map(m => (
                      <button key={m.val} onClick={() => setCreditMethod(m.val)}
                        className={`flex-1 h-11 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                          creditMethod === m.val ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-700'
                        }`} data-testid={`credit-method-${m.val}`}>
                        <m.icon size={16} /> {m.label}
                      </button>
                    ))}
                  </div>
                  <div className="bg-orange-50 rounded-xl p-3 flex justify-between items-center">
                    <span className="text-xs text-[#FF9500]">Kalan Borç</span>
                    <span className="text-base font-bold text-[#FF3B30]">{veresiyeRemaining > 0 ? `${veresiyeRemaining.toFixed(0)} TL` : '0 TL'}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    {[
                      { val: 'cash', label: 'Nakit', icon: Banknote },
                      { val: 'card', label: 'Kart', icon: CreditCard },
                      { val: 'split', label: 'Parçalı', icon: null },
                    ].map(m => (
                      <button key={m.val} onClick={() => setPaymentMethod(m.val)}
                        className={`flex-1 h-12 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                          paymentMethod === m.val ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-700'
                        }`} data-testid={`payment-${m.val}`}>
                        {m.icon && <m.icon size={16} />} {m.label}
                      </button>
                    ))}
                  </div>

                  {paymentMethod === 'split' && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <label className="text-[10px] text-[#8E8E93] mb-0.5 block">Nakit</label>
                          <Input type="number" placeholder="0" value={cashAmount}
                            onChange={e => { setCashAmount(e.target.value); setCardAmount(String(total - (parseFloat(e.target.value) || 0))); }}
                            className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl pr-6 text-right"
                            data-testid="split-cash-input" />
                          <span className="absolute right-2 bottom-2.5 text-[10px] text-[#8E8E93]">TL</span>
                        </div>
                        <div className="flex-1 relative">
                          <label className="text-[10px] text-[#8E8E93] mb-0.5 block">Kart</label>
                          <Input type="number" placeholder="0" value={cardAmount}
                            onChange={e => { setCardAmount(e.target.value); setCashAmount(String(total - (parseFloat(e.target.value) || 0))); }}
                            className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl pr-6 text-right"
                            data-testid="split-card-input" />
                          <span className="absolute right-2 bottom-2.5 text-[10px] text-[#8E8E93]">TL</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 lg:p-6 border-t border-gray-100 shrink-0">
          {step === 1 && (
            <Button onClick={() => setStep(2)} disabled={cart.length === 0}
              className="w-full h-12 bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl text-sm font-semibold disabled:opacity-40"
              data-testid="step1-next">
              Devam ({total.toFixed(0)} TL) <ChevronRight size={16} className="ml-1" />
            </Button>
          )}
          {step === 3 && (
            <Button onClick={handleSubmit} disabled={saving}
              className="w-full h-12 bg-[#34C759] hover:bg-[#2DB44E] rounded-xl text-sm font-semibold disabled:opacity-40"
              data-testid="complete-sale-btn">
              {saving ? 'Kaydediliyor...' : (
                <><Check size={16} className="mr-1" /> Satışı Tamamla ({total.toFixed(0)} TL)</>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickSaleModal;
